import { useEffect, useRef } from 'react';

export const AUTO_APPLY_DEBOUNCE_MS = 500;

interface UseAutoApplyEffectParams {
  /** Whether the auto-apply setting is enabled */
  autoApply: boolean;
  /** Selected measurement path, if any */
  measurementName?: string | null;
  /** Number of selected metrics */
  metricsCount: number;
  /** True while the filter modal is open */
  isEditingFilters: boolean;
  /** True while a metric/dimension popover is open */
  isPopoverOpen: boolean;
  /** Serialized snapshot of query-relevant fields; a change signals a re-run is needed */
  queryFieldsKey: string;
  /** Executes the query (kept in a ref internally so identity changes don't retrigger) */
  onRun: () => void;
}

/**
 * Debounced auto-apply: runs `onRun` when `queryFieldsKey` changes and all gates
 * allow it (auto-apply on, a measurement with metrics selected, no filter modal or
 * metric/dimension popover open). Skips the initial mount and skips re-runs when a
 * gate closes without any query-relevant field actually changing.
 */
export function useAutoApplyEffect({
  autoApply,
  measurementName,
  metricsCount,
  isEditingFilters,
  isPopoverOpen,
  queryFieldsKey,
  onRun
}: UseAutoApplyEffectParams): void {
  const isInitial = useRef(true);
  const lastAppliedKey = useRef<string | null>(null);
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;

  const canRun = autoApply && !!measurementName && metricsCount > 0 && !isEditingFilters && !isPopoverOpen;

  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
      lastAppliedKey.current = queryFieldsKey;
      return undefined;
    }

    if (!canRun) {
      return undefined;
    }

    if (queryFieldsKey === lastAppliedKey.current) {
      return undefined;
    }

    const timer = setTimeout(() => {
      lastAppliedKey.current = queryFieldsKey;
      onRunRef.current();
    }, AUTO_APPLY_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [queryFieldsKey, canRun]);
}
